import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/esm/Add';
import CheckIcon from '@mui/icons-material/esm/Check';
import CloseIcon from '@mui/icons-material/esm/Close';
import DoneAllIcon from '@mui/icons-material/esm/DoneAll';
import QrCodeScannerIcon from '@mui/icons-material/esm/QrCodeScanner';
import SendIcon from '@mui/icons-material/esm/Send';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import EditIcon from '@mui/icons-material/esm/Edit';
import IconButton from '@mui/material/IconButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { PageHeader } from '../../components/layout';
import BarcodeScanner from '../../components/field/BarcodeScanner';
import procurementService, {
  GoodsReceipt,
  GoodsReceiptItemDTO,
  PurchaseOrder,
  PurchaseOrderItemDTO,
  PurchaseOrderStatus,
  ProcurementCatalog,
} from '../../services/api/procurementService';
import { findLineByMaterialCode } from '../../utils/materialCodeLookup';

const PO_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  sent: 'Sent',
  confirmed: 'Confirmed',
  partial_received: 'Partial received',
  received: 'Received',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function dateOnlyTime(value?: string | null): number {
  if (!value) return Number.NaN;
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return Number.NaN;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function isTodayDate(value?: string | null): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dateOnlyTime(value) === today.getTime();
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', maximumFractionDigits: 0 }).format(value || 0);
}

function hasRole(role: string | undefined, allowed: string[]) {
  return Boolean(role && (role === 'admin' || allowed.includes(role)));
}

function poHasExpectedDeliveryDate(po: PurchaseOrder): boolean {
  return Boolean(po.expectedDeliveryDate || (po.items?.length && po.items.every((item) => Boolean(item.expectedDeliveryDate))));
}

function lifecycleActions(po: PurchaseOrder | null, role: string | undefined) {
  if (!po) return [];
  const actions: Array<{ key: string; label: string; icon: JSX.Element; reasonRequired?: boolean; run: (reason?: string) => Promise<PurchaseOrder> }> = [];
  if (po.status === 'draft' && hasRole(role, ['pm', 'procurement'])) {
    actions.push({ key: 'submit', label: 'Submit', icon: <SendIcon />, run: () => procurementService.submitPurchaseOrder(po.id) });
  }
  if (po.status === 'pending_approval' && hasRole(role, ['accounts'])) {
    actions.push({ key: 'approve', label: 'Approve', icon: <CheckIcon />, run: () => procurementService.approvePurchaseOrder(po.id) });
    actions.push({ key: 'reject', label: 'Reject', icon: <CloseIcon />, reasonRequired: true, run: (reason) => procurementService.rejectPurchaseOrder(po.id, reason) });
  }
  if (po.status === 'approved' && hasRole(role, ['pm', 'procurement']) && poHasExpectedDeliveryDate(po)) {
    actions.push({ key: 'send', label: 'Send', icon: <SendIcon />, run: () => procurementService.sendPurchaseOrder(po.id) });
  }
  if (po.status === 'sent' && hasRole(role, ['pm', 'procurement']) && poHasExpectedDeliveryDate(po)) {
    actions.push({ key: 'confirm', label: 'Confirm', icon: <CheckIcon />, run: () => procurementService.confirmPurchaseOrder(po.id) });
  }
  if (['draft', 'pending_approval', 'approved', 'sent', 'confirmed'].includes(po.status) && hasRole(role, ['pm', 'procurement'])) {
    actions.push({ key: 'cancel', label: 'Cancel', icon: <CloseIcon />, reasonRequired: true, run: (reason) => procurementService.cancelPurchaseOrder(po.id, reason) });
  }
  if (po.status === 'partial_received' && hasRole(role, ['accounts'])) {
    actions.push({ key: 'close-short', label: 'Close short', icon: <DoneAllIcon />, run: () => procurementService.closeShortPurchaseOrder(po.id) });
  }
  if (
    po.status === 'received' &&
    hasRole(role, ['accounts']) &&
    (po.accountsTransactions ?? []).some((row) => ['recorded', 'reconciled'].includes(row.status))
  ) {
    actions.push({ key: 'close', label: 'Close', icon: <DoneAllIcon />, run: () => procurementService.closePurchaseOrder(po.id) });
  }
  return actions;
}

interface PurchaseOrderDetailPageProps {
  fieldMode?: boolean;
}

interface EditPoLine {
  id?: string;
  resourceId: string;
  storageLocationId: string;
  quantityOrdered: number;
  unitCost: number;
  expectedDeliveryDate: string;
  description: string;
}

export default function PurchaseOrderDetailPage({ fieldMode = false }: PurchaseOrderDetailPageProps) {
  const { purchaseOrderId } = useParams();
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [catalog, setCatalog] = useState<ProcurementCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [grnOpen, setGrnOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [grnNotice, setGrnNotice] = useState<string | null>(null);
  const [scannedLineId, setScannedLineId] = useState<string | null>(null);
  const [reasonAction, setReasonAction] = useState<null | { title: string; run: (reason: string) => Promise<unknown> }>(null);
  const [reason, setReason] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [removedLineIds, setRemovedLineIds] = useState<string[]>([]);
  const [editForm, setEditForm] = useState<{
    supplierId: string;
    expectedDeliveryDate: string;
    notes: string;
    items: EditPoLine[];
  }>({ supplierId: '', expectedDeliveryDate: '', notes: '', items: [] });
  const [grnForm, setGrnForm] = useState<{
    grnNumber: string;
    receivedAt: string;
    notes: string;
    items: Array<{ purchaseOrderItemId: string; storageLocationId: string; quantityReceived: number; quantityAccepted: number; quantityRejected: number; rejectionReason: string }>;
  }>({ grnNumber: '', receivedAt: '', notes: '', items: [] });
  const selectedEditResourceIds = editForm.items.map((line) => line.resourceId).filter(Boolean);
  const duplicateEditResourceIds = new Set(selectedEditResourceIds.filter((id, index) => selectedEditResourceIds.indexOf(id) !== index));
  const hasDuplicateEditItems = duplicateEditResourceIds.size > 0;
  const hasDeliveryReceivedToday = (po?.goodsReceipts ?? [])
    .some((grn) => grn.status !== 'rejected' && isTodayDate(grn.receivedAt || grn.createdAt));

  const storageById = useMemo(
    () => new Map((catalog?.storageLocations ?? []).map((location) => [location.id, location])),
    [catalog]
  );

  const load = async () => {
    if (!purchaseOrderId) return;
    try {
      setLoading(true);
      setError(null);
      const [poResult, catalogResult] = await Promise.all([
        procurementService.getPurchaseOrder(purchaseOrderId),
        procurementService.getCatalog(),
      ]);
      setPo(poResult);
      setCatalog(catalogResult);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [purchaseOrderId]);

  const openGrn = () => {
    const items = (po?.items ?? [])
      .filter((item) => Number(item.quantityOrdered) > Number(item.quantityReceived || 0))
      .map((item) => {
        const remaining = Math.max(0, Number(item.quantityOrdered || 0) - Number(item.quantityReceived || 0));
        return {
          purchaseOrderItemId: item.id,
          storageLocationId: item.storageLocationId || '',
          quantityReceived: remaining,
          quantityAccepted: remaining,
          quantityRejected: 0,
          rejectionReason: '',
        };
      });
    setGrnForm({ grnNumber: '', receivedAt: new Date().toISOString().slice(0, 10), notes: '', items });
    setGrnNotice(null);
    setScannedLineId(null);
    setGrnOpen(true);
  };

  const updateGrnLine = (index: number, patch: Partial<typeof grnForm.items[number]>) => {
    setGrnForm((current) => ({
      ...current,
      items: current.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const runAction = async (action: () => Promise<PurchaseOrder>) => {
    try {
      setSaving(true);
      setError(null);
      setPo(await action());
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const createGrn = async () => {
    if (!po) return;
    try {
      setSaving(true);
      setError(null);
      await procurementService.createGoodsReceipt({
        purchaseOrderId: po.id,
        grnNumber: grnForm.grnNumber || undefined,
        receivedAt: grnForm.receivedAt || undefined,
        notes: grnForm.notes || undefined,
        items: grnForm.items
          .filter((item) => item.quantityReceived > 0)
          .map((item) => ({
            purchaseOrderItemId: item.purchaseOrderItemId,
            storageLocationId: item.storageLocationId,
            quantityReceived: Number(item.quantityReceived),
            quantityAccepted: Number(item.quantityAccepted),
            quantityRejected: Number(item.quantityRejected),
            rejectionReason: item.rejectionReason || undefined,
          })),
      });
      setGrnOpen(false);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create delivery record');
    } finally {
      setSaving(false);
    }
  };

  const handleMaterialScan = (code: string) => {
    if (!po) return;
    const poItem = findLineByMaterialCode(po.items ?? [], code);
    if (!poItem) {
      setGrnNotice(`Material code ${code.trim()} is not on this purchase order.`);
      setScannedLineId(null);
      setScannerOpen(false);
      return;
    }

    const index = grnForm.items.findIndex((item) => item.purchaseOrderItemId === poItem.id);
    if (index < 0) {
      setGrnNotice(`${poItem.resource?.resourceCode ?? code.trim()} has no remaining quantity to receive.`);
      setScannedLineId(null);
      setScannerOpen(false);
      return;
    }

    const remaining = Math.max(0, Number(poItem.quantityOrdered || 0) - Number(poItem.quantityReceived || 0));
    updateGrnLine(index, {
      quantityReceived: remaining,
      quantityAccepted: remaining,
      quantityRejected: 0,
    });
    setScannedLineId(poItem.id);
    setGrnNotice(`${poItem.resource?.resourceCode ?? code.trim()} matched ${poItem.resource?.name ?? poItem.resourceId}.`);
    setScannerOpen(false);
  };

  const handleSubmitGrn = async (grn: GoodsReceipt) => {
    try {
      setSaving(true);
      setError(null);
      await procurementService.submitGoodsReceipt(grn.id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to submit delivery record');
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptGrn = async (grn: GoodsReceipt) => {
    if (!window.confirm(`Accept ${grn.grnNumber}? This posts accepted quantities to inventory and cannot be casually undone.`)) return;
    try {
      setSaving(true);
      setError(null);
      await procurementService.acceptGoodsReceipt(grn.id, (grn.items ?? []).map((item: GoodsReceiptItemDTO) => ({
        id: item.id,
        quantityAccepted: Number(item.quantityAccepted || item.quantityReceived || 0),
        quantityRejected: Number(item.quantityRejected || 0),
        rejectionReason: item.rejectionReason || undefined,
      })));
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to accept delivery record');
    } finally {
      setSaving(false);
    }
  };

  const openEditPo = () => {
    if (!po) return;
    setRemovedLineIds([]);
    setEditForm({
      supplierId: po.supplierId,
      expectedDeliveryDate: po.expectedDeliveryDate?.slice(0, 10) ?? '',
      notes: po.notes ?? '',
      items: (po.items ?? []).map((item) => ({
        id: item.id,
        resourceId: item.resourceId,
        storageLocationId: item.storageLocationId ?? '',
        quantityOrdered: Number(item.quantityOrdered),
        unitCost: Number(item.unitCost),
        expectedDeliveryDate: item.expectedDeliveryDate?.slice(0, 10) ?? '',
        description: item.description ?? '',
      })),
    });
    setEditOpen(true);
  };

  const updateEditLine = (index: number, patch: Partial<EditPoLine>) => {
    setEditForm((current) => ({
      ...current,
      items: current.items.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line),
    }));
  };

  const removeEditLine = (index: number) => {
    setEditForm((current) => {
      const line = current.items[index];
      if (line?.id) setRemovedLineIds((ids) => [...ids, line.id!]);
      return { ...current, items: current.items.filter((_, lineIndex) => lineIndex !== index) };
    });
  };

  const saveEditPo = async () => {
    if (!po || editForm.items.length === 0) return;
    try {
      setSaving(true);
      setError(null);
      await procurementService.updatePurchaseOrder(po.id, {
        supplierId: editForm.supplierId,
        expectedDeliveryDate: editForm.expectedDeliveryDate,
        notes: editForm.notes,
      });
      for (const lineId of removedLineIds) {
        await procurementService.removePurchaseOrderItem(lineId);
      }
      for (const line of editForm.items) {
        const payload = {
          resourceId: line.resourceId,
          storageLocationId: line.storageLocationId || undefined,
          quantityOrdered: Number(line.quantityOrdered),
          unitCost: Number(line.unitCost),
          expectedDeliveryDate: line.expectedDeliveryDate || undefined,
          description: line.description || undefined,
        };
        if (line.id) await procurementService.updatePurchaseOrderItem(line.id, payload);
        else await procurementService.addPurchaseOrderItem(po.id, payload);
      }
      setEditOpen(false);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to update purchase order');
    } finally {
      setSaving(false);
    }
  };

  const runReasonAction = async () => {
    if (!reasonAction || !reason.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await reasonAction.run(reason.trim());
      setReasonAction(null);
      setReason('');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const canCreateGrn = Boolean(po && hasRole(role, ['stores']) && ['confirmed', 'partial_received'].includes(po.status));
  const backPath = !fieldMode
    ? '/procurement'
    : role === 'accounts'
      ? '/field/accounts/purchase-orders'
      : role === 'stores'
        ? '/field/stores/receiving'
        : '/field/procurement/orders';

  return (
    <Stack spacing={2}>
      <PageHeader
        title={po ? po.poNumber : fieldMode ? 'Operational purchase order' : 'Purchase order'}
        subtitle={po ? `${po.supplier?.name || po.supplierId} - ${PO_STATUS_LABELS[po.status]}` : 'Loading purchase order'}
      />

      <Button component={RouterLink} to={backPath} sx={{ alignSelf: 'flex-start' }}>
        Back to workspace
      </Button>

      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !po && <Alert severity="warning">Purchase order not found.</Alert>}

      {po && (
        <>
          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="overline" color="text.secondary">Status</Typography>
                <Box><Chip label={PO_STATUS_LABELS[po.status]} /></Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="overline" color="text.secondary">Expected delivery</Typography>
                <Typography>{formatDate(po.expectedDeliveryDate)}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="overline" color="text.secondary">Total</Typography>
                <Typography>{formatCurrency(Number(po.totalAmount || 0))}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="overline" color="text.secondary">Threshold</Typography>
                <Typography>{formatCurrency(Number(po.approvalThresholdAtCreation || 0))}</Typography>
              </Grid>
            </Grid>
            {role === 'accounts' && po.status === 'received' && !(po.accountsTransactions ?? []).some((row) => ['recorded', 'reconciled'].includes(row.status)) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This PO cannot be financially closed until the linked receipt transaction has been recorded or reconciled in Accounts → Transactions.
              </Alert>
            )}
            {hasRole(role, ['stores']) && hasDeliveryReceivedToday && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Delivery received today.
              </Alert>
            )}
            {['approved', 'sent'].includes(po.status) && hasRole(role, ['pm', 'procurement']) && !poHasExpectedDeliveryDate(po) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Add an expected delivery date before sending or confirming this PO so Stores knows which receiving date to use.
              </Alert>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
              {po.status === 'draft' && hasRole(role, ['pm', 'procurement']) && (
                <Button size="small" variant="outlined" startIcon={<EditIcon />} disabled={saving} onClick={openEditPo}>Edit PO</Button>
              )}
              {lifecycleActions(po, role).map((action) => (
                <Button
                  key={action.key}
                  size="small"
                  variant="outlined"
                  startIcon={action.icon}
                  disabled={saving}
                  onClick={() => action.reasonRequired
                    ? (setReason(''), setReasonAction({ title: `${action.label} ${po.poNumber}`, run: (value) => action.run(value) }))
                    : runAction(() => action.run())}
                >
                  {action.label}
                </Button>
              ))}
              {canCreateGrn && (
                <Button size="small" variant="contained" startIcon={<AddIcon />} disabled={saving} onClick={openGrn}>
                  Record delivery received
                </Button>
              )}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Lines</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Resource</TableCell>
                  <TableCell>Storage</TableCell>
                  <TableCell align="right">Ordered</TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right">Unit Cost</TableCell>
                  <TableCell align="right">Linked Tasks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(po.items ?? []).map((item: PurchaseOrderItemDTO) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{item.resource?.name || item.resourceId}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.resource?.resourceCode || item.description || ''}</Typography>
                    </TableCell>
                    <TableCell>{item.storageLocation?.name || (item.storageLocationId ? storageById.get(item.storageLocationId)?.name : '-') || '-'}</TableCell>
                    <TableCell align="right">{item.quantityOrdered}</TableCell>
                    <TableCell align="right">{item.quantityReceived}</TableCell>
                    <TableCell align="right">{formatCurrency(Number(item.unitCost || 0))}</TableCell>
                    <TableCell align="right">{item.taskResourceLinks?.length ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Delivery records</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Delivery note</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell align="right">Lines</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(po.goodsReceipts ?? []).map((grn: GoodsReceipt) => (
                  <TableRow key={grn.id}>
                    <TableCell>{grn.grnNumber}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={grn.status.replace(/_/g, ' ')} />
                        {grn.status !== 'rejected' && isTodayDate(grn.receivedAt || grn.createdAt) && (
                          <Chip size="small" color="success" label="Delivery received" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDate(grn.receivedAt)}</TableCell>
                    <TableCell align="right">{grn.items?.length ?? 0}</TableCell>
                    <TableCell align="right">
                      {grn.status === 'draft' && hasRole(role, ['stores']) && (
                        <Button size="small" onClick={() => handleSubmitGrn(grn)} disabled={saving}>Submit</Button>
                      )}
                      {['pending_acceptance', 'disputed'].includes(grn.status) && hasRole(role, ['stores']) && (
                        <Button size="small" startIcon={<CheckIcon />} onClick={() => handleAcceptGrn(grn)} disabled={saving}>Accept</Button>
                      )}
                      {grn.status === 'pending_acceptance' && hasRole(role, ['stores']) && (
                        <Button size="small" color="warning" onClick={() => {
                          setReason('');
                          setReasonAction({ title: `Dispute ${grn.grnNumber}`, run: (value) => procurementService.flagDisputeGoodsReceipt(grn.id, value) });
                        }} disabled={saving}>Dispute</Button>
                      )}
                      {['pending_acceptance', 'disputed'].includes(grn.status) && hasRole(role, ['stores']) && (
                        <Button size="small" color="error" onClick={() => {
                          setReason('');
                          setReasonAction({ title: `Reject ${grn.grnNumber}`, run: (value) => procurementService.rejectGoodsReceipt(grn.id, value) });
                        }} disabled={saving}>Reject</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {(po.goodsReceipts ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}><Alert severity="info">No delivery records have been recorded for this PO.</Alert></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {role === 'accounts' && (
            <Paper sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Linked financial records</Typography>
              <Table size="small"><TableHead><TableRow><TableCell>Date</TableCell><TableCell>Description</TableCell><TableCell>Status</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead><TableBody>
                {(po.accountsTransactions ?? []).map((row) => <TableRow key={row.id}><TableCell>{formatDate(row.transactionDate)}</TableCell><TableCell>{row.description}</TableCell><TableCell><Chip size="small" label={row.status} /></TableCell><TableCell align="right">{formatCurrency(Number(row.amount))}</TableCell></TableRow>)}
                {(po.accountsTransactions ?? []).length === 0 && <TableRow><TableCell colSpan={4}><Alert severity="info">No transaction is linked to this purchase order.</Alert></TableCell></TableRow>}
              </TableBody></Table>
            </Paper>
          )}

          <Dialog open={grnOpen} onClose={() => setGrnOpen(false)} maxWidth="lg" fullWidth>
            <DialogTitle>Record delivery received</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Button variant="outlined" startIcon={<QrCodeScannerIcon />} onClick={() => setScannerOpen(true)}>
                    Scan Material
                  </Button>
                  {grnNotice && <Alert severity={scannedLineId ? 'success' : 'warning'} sx={{ flex: 1 }}>{grnNotice}</Alert>}
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Delivery note number" value={grnForm.grnNumber} onChange={(event) => setGrnForm({ ...grnForm, grnNumber: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth type="date" label="Received at" value={grnForm.receivedAt} InputLabelProps={{ shrink: true }} onChange={(event) => setGrnForm({ ...grnForm, receivedAt: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Notes" value={grnForm.notes} onChange={(event) => setGrnForm({ ...grnForm, notes: event.target.value })} />
                  </Grid>
                </Grid>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Resource</TableCell>
                      <TableCell>Storage</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell align="right">Accepted</TableCell>
                      <TableCell align="right">Rejected</TableCell>
                      <TableCell>Rejection reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grnForm.items.map((line, index) => {
                      const poItem = po.items?.find((item) => item.id === line.purchaseOrderItemId);
                      return (
                        <TableRow
                          key={line.purchaseOrderItemId}
                          selected={scannedLineId === line.purchaseOrderItemId}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{poItem?.resource?.name || poItem?.resourceId}</Typography>
                            <Typography variant="caption" color="text.secondary">{poItem?.resource?.resourceCode || ''}</Typography>
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <InputLabel>Storage</InputLabel>
                              <Select value={line.storageLocationId} label="Storage" onChange={(event) => updateGrnLine(index, { storageLocationId: event.target.value })}>
                                {(catalog?.storageLocations ?? []).map((location) => (
                                  <MenuItem key={location.id} value={location.id}>{location.locationCode} - {location.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="right">
                            <TextField size="small" type="number" value={line.quantityReceived} onChange={(event) => {
                              const quantityReceived = Number(event.target.value);
                              updateGrnLine(index, { quantityReceived, quantityAccepted: quantityReceived });
                            }} />
                          </TableCell>
                          <TableCell align="right">
                            <TextField size="small" type="number" value={line.quantityAccepted} onChange={(event) => updateGrnLine(index, { quantityAccepted: Number(event.target.value) })} />
                          </TableCell>
                          <TableCell align="right">
                            <TextField size="small" type="number" value={line.quantityRejected} onChange={(event) => updateGrnLine(index, { quantityRejected: Number(event.target.value) })} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" value={line.rejectionReason} onChange={(event) => updateGrnLine(index, { rejectionReason: event.target.value })} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setGrnOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={createGrn} disabled={saving || grnForm.items.length === 0}>
                Create
              </Button>
            </DialogActions>
          </Dialog>
          <BarcodeScanner
            open={scannerOpen}
            title="Scan Material Code"
            onClose={() => setScannerOpen(false)}
            onScan={handleMaterialScan}
          />
          <Dialog open={Boolean(reasonAction)} onClose={() => !saving && setReasonAction(null)} maxWidth="sm" fullWidth>
            <DialogTitle>{reasonAction?.title}</DialogTitle>
            <DialogContent>
              <TextField autoFocus fullWidth required multiline minRows={3} label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} sx={{ mt: 1 }} helperText="A reason is required and will be retained with the audit record." />
            </DialogContent>
            <DialogActions><Button onClick={() => setReasonAction(null)} disabled={saving}>Cancel</Button><Button variant="contained" color="error" onClick={runReasonAction} disabled={saving || !reason.trim()}>Confirm</Button></DialogActions>
          </Dialog>
          <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="lg" fullWidth>
            <DialogTitle>Edit draft purchase order</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Supplier</InputLabel><Select value={editForm.supplierId} label="Supplier" onChange={(event) => setEditForm({ ...editForm, supplierId: event.target.value })}>{(catalog?.suppliers ?? []).map((supplier) => <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth type="date" label="Expected delivery" InputLabelProps={{ shrink: true }} value={editForm.expectedDeliveryDate} onChange={(event) => setEditForm({ ...editForm, expectedDeliveryDate: event.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Notes" value={editForm.notes} onChange={(event) => setEditForm({ ...editForm, notes: event.target.value })} /></Grid>
                </Grid>
                {hasDuplicateEditItems && (
                  <Alert severity="warning">
                    This resource is already on the PO. Update the quantity on the existing row instead of adding it again.
                  </Alert>
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="h6" fontWeight={800}>Lines</Typography><Button startIcon={<AddIcon />} onClick={() => setEditForm((current) => ({ ...current, items: [...current.items, { resourceId: '', storageLocationId: '', quantityOrdered: 1, unitCost: 0, expectedDeliveryDate: '', description: '' }] }))}>Add line</Button></Stack>
                <Box sx={{ overflowX: 'auto' }}><Table size="small"><TableHead><TableRow><TableCell>Resource</TableCell><TableCell>Storage</TableCell><TableCell>Quantity</TableCell><TableCell>Unit cost</TableCell><TableCell>Expected</TableCell><TableCell /></TableRow></TableHead><TableBody>
                  {editForm.items.map((line, index) => <TableRow key={line.id ?? `new-${index}`}><TableCell><FormControl fullWidth size="small" sx={{ minWidth: 220 }}><InputLabel>Resource</InputLabel><Select value={line.resourceId} label="Resource" onChange={(event) => updateEditLine(index, { resourceId: event.target.value, unitCost: Number(catalog?.resources.find((resource) => resource.id === event.target.value)?.unitCost ?? line.unitCost) })}>{(catalog?.resources ?? []).map((resource) => <MenuItem key={resource.id} value={resource.id}>{resource.resourceCode} · {resource.name}</MenuItem>)}</Select></FormControl></TableCell><TableCell><FormControl fullWidth size="small" sx={{ minWidth: 170 }}><InputLabel>Storage</InputLabel><Select value={line.storageLocationId} label="Storage" onChange={(event) => updateEditLine(index, { storageLocationId: event.target.value })}><MenuItem value="">PO default</MenuItem>{(catalog?.storageLocations ?? []).map((location) => <MenuItem key={location.id} value={location.id}>{location.locationCode} · {location.name}</MenuItem>)}</Select></FormControl></TableCell><TableCell><TextField size="small" type="number" value={line.quantityOrdered} onChange={(event) => updateEditLine(index, { quantityOrdered: Number(event.target.value) })} /></TableCell><TableCell><TextField size="small" type="number" value={line.unitCost} onChange={(event) => updateEditLine(index, { unitCost: Number(event.target.value) })} /></TableCell><TableCell><TextField size="small" type="date" InputLabelProps={{ shrink: true }} value={line.expectedDeliveryDate} onChange={(event) => updateEditLine(index, { expectedDeliveryDate: event.target.value })} /></TableCell><TableCell><IconButton color="error" onClick={() => removeEditLine(index)} disabled={editForm.items.length === 1}><DeleteIcon /></IconButton></TableCell></TableRow>)}
                </TableBody></Table></Box>
              </Stack>
            </DialogContent>
            <DialogActions><Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={saveEditPo} disabled={saving || !editForm.supplierId || editForm.items.length === 0 || hasDuplicateEditItems || editForm.items.some((line) => !line.resourceId || line.quantityOrdered <= 0)}>Save changes</Button></DialogActions>
          </Dialog>
        </>
      )}
    </Stack>
  );
}
